import React, {useMemo, useRef, useState} from 'react';
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import projectUtils from "../../../../utils/project-utils.js";
import {useQuery} from "@tanstack/react-query";
import DeviceService from "../../../../services/web/device-service.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const initialFilter = {
    name: "",
    phone: "",
    agent: false,
    monitoramento: false,
    pcl: false,
}

const selectOpt = [
    {value: "true", label: "Autorizado"},
    {value: "false", label: "Não autorizado"},
]
const DeviceList = () => {
    const navigate = navigateHook();
    const hasPermission = useHasPermission();

    const hasUserPermission = hasPermission(PermissionEnum.USER)
    const [state, setState] = useState(initialFilter)
    const [filter, setFilter] = useState([]);
    const gridRef = useRef(null);
    const theme = useMemo(() => {
        return aggridUtils.generalTheme();
    }, []);

    const { data, isLoading } = useQuery({
        queryFn: () =>  DeviceService.list(filter).then(({ data }) => data.data ?? []),
        queryKey: ['route', filter]
    });

    const handleNavigate = (id, url) => {
        let isBlank = true;
        let urlDev = `/${url}/form/${id}`;
        navigate(urlDev, isBlank);
    }

    const columnDefs = [
        {headerName: "Nome", field: "user_name", flex: 1, comparator: aggridUtils.nullLastComparator, sort: "asc",
            cellRenderer: params => {
                if (hasUserPermission) {
                    return (<span onClick={() => handleNavigate(params.data.user_id, "user-agents")} className="underline cursor-pointer">
                        {params.data.user_name}</span>)
                }
                return params.data.user_name
            }
        },
        {headerName: "Telefone", field: "phone", flex: 1, comparator: aggridUtils.nullLastComparator,
            valueGetter: (params) => {
                return projectUtils.formatPhone(params.data.phone)
            },
        },
        {headerName: "Sentry Agente", field: "agent_dt_activation", flex: 1,
            comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                const newA = valueA === "Não autorizado" ? null : valueA;
                const newB = valueB === "Não autorizado" ? null : valueB;

                return aggridUtils.nullLastComparator(newA, newB, nodeA, nodeB, isDescending);
            }
        },
        {headerName: "Sentry Alarmes", field: "monitoramento_dt_activation", flex: 1,
            comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                const newA = valueA === "Não autorizado" ? null : valueA;
                const newB = valueB === "Não autorizado" ? null : valueB;

                return aggridUtils.nullLastComparator(newA, newB, nodeA, nodeB, isDescending);
            }
        },
        {headerName: "Associações", field: "associations", flex: 1, comparator: aggridUtils.nullLastComparator,
            cellRenderer: params => {
                const associations = params.data.associations
                return (<span onClick={() => handleNavigate(params.data.id, "device")} className="underline cursor-pointer">
                        {associations ? associations : 0} {(associations > 1 || associations === null) ? 'associações' : 'associação'}</span>)
            }
        },
        {headerName: "Sentry Captura Móvel", field: "pcl_dt_activation", flex: 1,
            comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                const newA = valueA === "Não autorizado" ? null : valueA;
                const newB = valueB === "Não autorizado" ? null : valueB;

                return aggridUtils.nullLastComparator(newA, newB, nodeA, nodeB, isDescending);
            }
        }
    ];

    const onChange = ({target}) => {
        let {id, value} = target;

        setState(prev => ({...prev, [id]: value}));
    }

    const handleInputs = (id, value) => setState(prev => ({...prev, [id]: value}));

    const apply = () => {
        gridRef.current?.api?.showLoadingOverlay();

        setFilter({
            user_name: state.name,
            phone: state.phone.replace(/\D/g, ""),
            monitoramento_authorized: state.monitoramento ?? "",
            pcl_authorized: state.pcl ?? "",
            agent_authorized: state.agent ?? "",
        })
    }

    const clearFilter = () => {
        setState(initialFilter);
        setFilter([]);
    }

    const exportData = () => {
        const csvMaker = (data) => Object.values(data).join(';');

        const array = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort(({data}) => {
            let pcl
            const pclLane = data.pcl_dt_activation !== null && data.lanecode ? " (" + data.lanecode + ")" + data.lanedescription : "";

            if (data.pcl_dt_activation === 'Autorizado') {
                pcl = data.pcl_dt_activation;
            }else{
                pcl = data.pcl_dt_activation + pclLane;
            }

            const cloneArray =  {
                "Nome": data.user_name,
                "Telefone": projectUtils.formatPhone(data.phone),
                "Sentry Agente": data.agent_dt_activation,
                "Sentry Alarmes": data.monitoramento_dt_activation,
                "Associações": (data.associations ?? 0) + " associações",
                "Sentry Captura Móvel": pcl,
            };

            if (!data.total) delete cloneArray.Total;
            array.push(cloneArray);
        });

        projectUtils.exportCsvData(csvMaker, array)
    }

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clearFilter);
    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Dispositivos</Template.Title>

                <Button target="success" className="ms-2" onClick={exportData} title="Exportar">
                    <FaFileExcel/> Exportar
                </Button>
            </Template.Header>

            <Template.Body className="flex flex-col h-full min-h-0 space-y-2">
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-4">
                            <InputForm
                                id="name"
                                classNameWrapper={"col-span-3"}
                                value={state.name}
                                onChange={onChange}
                                label="Nome do agente"
                            />

                            <InputForm
                                id="phone"
                                classNameWrapper={"col-span-2"}
                                value={state.phone}
                                onChange={onChange}
                                mask={MaskDefault.phone}
                                label="Telefone"
                            />

                            <SelectComponent
                                id="agent"
                                classNameWrapper={"col-span-2"}
                                value={state.agent}
                                options={selectOpt ?? []}
                                label={"Sentry Agente"}
                                onChange={(selected) => handleInputs("agent", selected?.value)}
                            />

                            <SelectComponent
                                id="monitoramento"
                                classNameWrapper={"col-span-2"}
                                value={state.monitoramento}
                                options={selectOpt ?? []}
                                label={"Sentry Alarmes"}
                                onChange={(selected) => handleInputs("monitoramento", selected?.value)}
                            />

                            <div className="flex items-end gap-4 col-span-3">
                                <SelectComponent
                                    id="pcl"
                                    value={state.pcl}
                                    options={selectOpt ?? []}
                                    label={"Sentry Captura Móvel"}
                                    onChange={(selected) => handleInputs("pcl", selected?.value)}
                                    classNameWrapper={"w-full"}
                                />
                                <div className={"flex gap-2"}>
                                    <Button title="Limpar" onClick={() => clearFilter()} target={"primary-outline"}>
                                        <FaEraser />
                                    </Button>
                                    <Button title="Buscar" onClick={() => apply()}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div className="flex">
                            <div className="h-[calc(100vh-153px)] w-full">
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={data ?? []}
                                    columnDefs={columnDefs}
                                    pagination
                                    paginationPageSize={100}
                                    paginationPageSizeSelector={[20, 50, 100, 500]}
                                    animateRows={false}
                                    suppressCellFocus={true}
                                    gridOptions={{localeText: aggridUtils.localization()}}
                                    theme={theme}
                                />
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default DeviceList;